import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "../../lib/adminApi";
import { compressImage, detectAspect } from "../../lib/compressImage";
import { CATEGORIES } from "./AdminPortfolio";

const inputCls =
  "w-full bg-transparent border-b border-white/15 focus:border-champagne outline-none py-2.5 text-sm font-light text-ivory placeholder:text-stone-600 transition-colors duration-300";
const labelCls = "text-[9px] font-mono tracking-[0.25em] uppercase text-stone-500";

export default function PhotoUploader({ onUploaded }) {
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState("Weddings");
  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("");
  const [story, setStory] = useState("");
  const [progress, setProgress] = useState(null);

  const reset = () => {
    setFiles([]);
    setTitle("");
    setLocation("");
    setStory("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (files.length === 0) return toast.error("Choose at least one image");
    let done = 0;
    for (const raw of files) {
      setProgress({ done, total: files.length, name: raw.name });
      try {
        const [file, aspect] = await Promise.all([compressImage(raw), detectAspect(raw)]);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", category);
        fd.append("title", files.length > 1 ? `${title || "Untitled"} ${done + 1}` : title || raw.name.replace(/\.\w+$/, ""));
        fd.append("location", location);
        fd.append("story", story);
        fd.append("aspect", aspect);
        const { data } = await adminApi.post("/portfolio", fd);
        onUploaded(data);
        done += 1;
      } catch (err) {
        toast.error(`${raw.name}: ${formatApiErrorDetail(err.response?.data?.detail)}`);
      }
    }
    setProgress(null);
    if (done > 0) {
      toast.success(`${done} photo${done > 1 ? "s" : ""} added to your portfolio`);
      reset();
    }
  };

  return (
    <form onSubmit={submit} className="border border-white/10 bg-elevated p-6" data-testid="photo-upload-form">
      <p className="text-[10px] font-mono tracking-[0.25em] uppercase text-stone-500">Add Frames</p>
      <h2 className="mt-2 font-serif text-xl text-ivory">Upload <span className="italic text-champagne">Photographs</span></h2>

      <label
        htmlFor="photo-files"
        data-testid="photo-dropzone"
        className="mt-6 flex flex-col items-center justify-center gap-2 border border-dashed border-white/20 hover:border-champagne/60 p-8 cursor-pointer transition-colors text-center"
      >
        <UploadCloud size={22} className="text-champagne" />
        <span className="text-xs font-light text-stone-300">
          {files.length ? `${files.length} file${files.length > 1 ? "s" : ""} selected` : "Click to choose JPEG / PNG / WebP"}
        </span>
        <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-stone-600">Large images are optimised automatically</span>
        <input id="photo-files" ref={fileRef} data-testid="photo-file-input" type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => setFiles(Array.from(e.target.files))} />
      </label>

      <div className="mt-6 space-y-5">
        <div>
          <label htmlFor="photo-category" className={labelCls}>Category</label>
          <select id="photo-category" data-testid="photo-category-select" value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
            {CATEGORIES.map((c) => <option key={c} value={c} className="bg-surface">{c}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="photo-title" className={labelCls}>Title</label>
          <input id="photo-title" data-testid="photo-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} placeholder="Villa Balbiano Whispers" />
        </div>
        <div>
          <label htmlFor="photo-location" className={labelCls}>Location</label>
          <input id="photo-location" data-testid="photo-location-input" value={location} onChange={(e) => setLocation(e.target.value)} className={inputCls} placeholder="Lake Como, Italy" />
        </div>
        <div>
          <label htmlFor="photo-story" className={labelCls}>Story / Caption</label>
          <textarea id="photo-story" data-testid="photo-story-input" rows={2} value={story} onChange={(e) => setStory(e.target.value)} className={`${inputCls} resize-none`} placeholder="Golden hour shafts piercing vaulted stone corridors…" />
        </div>
      </div>

      <button
        type="submit"
        data-testid="photo-upload-submit"
        disabled={!!progress}
        className="mt-7 w-full bg-champagne text-obsidian px-8 py-3.5 text-[11px] font-mono tracking-[0.2em] uppercase hover:bg-ivory transition-colors duration-300 disabled:opacity-50"
      >
        {progress ? `Uploading ${progress.done + 1} / ${progress.total}…` : "Add to Portfolio"}
      </button>
      {progress && (
        <div className="mt-3 h-[2px] bg-white/10" data-testid="photo-upload-progress">
          <div className="h-full bg-champagne transition-all duration-500" style={{ width: `${(progress.done / progress.total) * 100}%` }} />
        </div>
      )}
    </form>
  );
}
